import {
  ATMIcon,
  AvatarName,
  BankIcon,
  CardIcon,
  CoinIcon,
  TransactionIcon as TransactionIconBrighty,
  PlusIcon,
  AvatarIcon,
  TimeIcon,
  ErrorCircleIcon,
  AvatarBadge,
  AvatarBadgeProps,
} from '@razz-team/ui-web';
import { ApiTransaction } from '../../openapi';
import { TransactionTypeDataEnum } from '../../utils/useTransactionDetails';
import { useMemo } from 'react';
import { AvatarLogo } from '../Logo/AvatarLogo';

interface Props {
  transaction: ApiTransaction;
  size?: 'small' | 'medium' | 'large' | number;
  bigIcon?: boolean;
}

export function TransactionIcon({ transaction, size = 'medium', bigIcon }: Props) {
  const iconSize = useMemo(
    () => (bigIcon ? { width: '40px', height: '40px' } : undefined),
    [bigIcon],
  );

  const subIconSize = useMemo(
    () => (bigIcon ? { width: '32px', height: '32px' } : { width: '16px', height: '16px' }),
    [bigIcon],
  );

  const iconIcon = useMemo(() => {
    switch (transaction.type) {
      case TransactionTypeDataEnum.CARD_ATM_WITHDRAWAL_OUT:
        return <ATMIcon sx={iconSize} />;
      case TransactionTypeDataEnum.CARD_PAYMENT_OUT:
        return <CardIcon sx={iconSize} />;
      case TransactionTypeDataEnum.OWN_TRANSFER_IN:
      case TransactionTypeDataEnum.OWN_TRANSFER_OUT:
        return <TransactionIconBrighty sx={iconSize} />;
      case TransactionTypeDataEnum.INTERNAL_TRANSFER_IN:
        return <CardIcon sx={iconSize} />;
      case TransactionTypeDataEnum.EXTERNAL_INCOMING_TRANSFER_IN:
        switch (transaction.origin.payer?.type) {
          case 'INDIVIDUAL':
            return <BankIcon sx={iconSize} />;
          case 'CRYPTO':
            return <CoinIcon sx={iconSize} />;
          default:
            return null;
        }
      case TransactionTypeDataEnum.EXTERNAL_INCOMING_TRANSFER_IN_ACQUIRING:
        return <PlusIcon sx={iconSize} />;
      case TransactionTypeDataEnum.EXTERNAL_OUTGOING_TRANSFER_OUT:
        switch (transaction.counterparty.beneficiary?.type) {
          case 'INDIVIDUAL':
            return <BankIcon sx={iconSize} />;
          case 'CRYPTO':
            return <CoinIcon sx={iconSize} />;
          case 'BUSINESS':
            return <BankIcon sx={iconSize} />;
          default:
            return null;
        }
      case TransactionTypeDataEnum.REWARD_IN:
      case TransactionTypeDataEnum.FEE_OUT:
      case TransactionTypeDataEnum.FEE_OUT_INVOICE_PAYMENT:
      case TransactionTypeDataEnum.FEE_OUT_CARD_ORDER:
        return <AvatarLogo size={bigIcon ? 96 : 40} />;
      default:
        return <TransactionIconBrighty sx={iconSize} />;
    }
  }, [transaction, bigIcon, iconSize]);
  const iconSrc = useMemo(() => {
    switch (transaction.type) {
      case TransactionTypeDataEnum.INTERNAL_TRANSFER_OUT:
        return transaction.counterparty?.customer?.picture || '';
      case TransactionTypeDataEnum.INTERNAL_TRANSFER_IN:
        return transaction.origin.customer?.picture || '';
      default:
        return '';
    }
  }, [transaction]);
  const iconSubicon = useMemo((): AvatarBadgeProps => {
    switch (transaction.state) {
      case 'PENDING':
        return {
          type: 'icon',
          icon: <TimeIcon color="info" sx={{ ...subIconSize }} />,
        };
      case 'FAILED':
        return {
          type: 'icon',
          icon: <ErrorCircleIcon sx={{ ...subIconSize }} color="tertiary" />,
        };
      default:
        switch (transaction.type) {
          case TransactionTypeDataEnum.INTERNAL_TRANSFER_OUT:
          case TransactionTypeDataEnum.INTERNAL_TRANSFER_IN:
            return {
              type: 'icon',
              icon: <AvatarLogo size={bigIcon ? 32 : 16} />,
            };
          case TransactionTypeDataEnum.EXTERNAL_INCOMING_TRANSFER_IN:
            return {
              type: 'currency',
              currency: transaction.origin.amount.currency,
            };
          case TransactionTypeDataEnum.EXTERNAL_OUTGOING_TRANSFER_OUT:
            return {
              type: 'currency',
              currency: transaction.counterparty.amount.currency,
            };
          default:
            return { type: 'icon', icon: null };
        }
    }
  }, [transaction, bigIcon, subIconSize]);

  if (
    transaction.type === TransactionTypeDataEnum.INTERNAL_TRANSFER_OUT ||
    transaction.type === TransactionTypeDataEnum.INTERNAL_TRANSFER_IN
  ) {
    return (
      <AvatarName
        src={iconSrc}
        name={transaction.counterparty?.customer?.name || ''}
        size={bigIcon ? 96 : size}
      />
    );
  }

  return (
    <AvatarBadge {...iconSubicon}>
      <AvatarIcon icon={iconIcon} size={bigIcon ? 96 : size} />
    </AvatarBadge>
  );
}
